import React, { useState } from 'react';
import { ChevronRight, ArrowLeft, Home, Instagram } from 'lucide-react';
import { prodottiContent, siteInfo } from '../data/mock';

const ProdottiSection = ({ onNavigateToService }) => {
  return (
    <div 
      className="min-h-screen pt-20 pb-12 bg-cover bg-center bg-fixed relative"
      style={{
        backgroundImage: `url('${siteInfo.dashboardImage}')`
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/90" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8">
        {/* Title */}
        <div className="mb-12 text-center">
          <h2 className="font-cinzel text-2xl md:text-3xl font-semibold text-amber-800 tracking-wide">
            {prodottiContent.title}
          </h2>
          <div className="w-full max-w-[200px] md:max-w-[240px] h-0.5 bg-amber-700 mt-4 mx-auto" />
        </div>

        {/* Services Grid - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {prodottiContent.services.map((service, idx) => (
            <button
              key={idx}
              onClick={() => onNavigateToService(service.id)}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-amber-200 shadow-md hover:shadow-xl hover:border-amber-400 transition-all text-left group"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-cinzel text-xl text-amber-800 group-hover:text-amber-900">
                  {service.title}
                </h3>
                <ChevronRight className="w-5 h-5 text-amber-600 group-hover:translate-x-1 transition-transform" />
              </div>
              <p className="text-stone-600 text-sm leading-relaxed mt-3">
                {service.description}
              </p>
            </button>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-stone-600 text-sm">
            Ogni progetto è unico. Contattaci per un preventivo personalizzato.
          </p>
        </div>
      </div>
    </div>
  );
};

// Service Detail Page Component
export const ServiceDetailPage = ({ serviceId, onBack, onHome, onNavigateToGallery }) => {
  const service = prodottiContent.services.find(s => s.id === serviceId);
  
  if (!service) return null;

  return (
    <div 
      className="min-h-screen pt-20 pb-12 bg-cover bg-center bg-fixed relative"
      style={{
        backgroundImage: `url('${siteInfo.dashboardImage}')`
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/90" />
      
      {/* Navigation Buttons */}
      <div className="fixed top-4 left-4 z-50 flex gap-2">
        <button
          onClick={onBack}
          className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white shadow-md transition-all hover:shadow-lg"
        >
          <ArrowLeft className="w-5 h-5 text-stone-700" />
        </button>
        <button
          onClick={onHome}
          className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white shadow-md transition-all hover:shadow-lg"
        >
          <Home className="w-5 h-5 text-stone-700" />
        </button>
      </div>
      
      <div className="relative z-10 max-w-2xl mx-auto px-4 md:px-8">
        {/* Title */}
        <div className="mb-8 text-center">
          <h2 className="font-cinzel text-2xl md:text-3xl font-semibold text-amber-800 tracking-wide">
            {service.title}
          </h2>
          <div className="w-full max-w-[280px] h-0.5 bg-amber-700 mt-4 mx-auto" />
        </div>

        {/* Full Description */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 md:p-8 border border-amber-200 shadow-lg mb-8">
          <p className="text-stone-700 leading-relaxed text-base md:text-lg">
            {service.fullDescription}
          </p>
        </div>

        {/* Second Description (if exists) */}
        {service.secondDescription && (
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 md:p-8 border border-amber-200 shadow-lg mb-8">
            <p className="text-stone-700 leading-relaxed text-base md:text-lg whitespace-pre-line">
              {service.secondDescription}
            </p>
          </div>
        )}

        {/* Benefits List (for Resinatura) */}
        {service.benefitsList && service.benefitsList.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 md:p-8 border border-amber-200 shadow-lg mb-8">
            <ul className="space-y-5">
              {service.benefitsList.map((benefit, idx) => (
                <li key={idx}>
                  <div className="flex items-start gap-2">
                    <span className="text-amber-700 font-bold">•</span>
                    <div>
                      <span className="font-bold text-amber-800">{benefit.title}:</span>
                      <span className="text-stone-700 ml-1">{benefit.description}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Features (only if has features) */}
        {service.features && service.features.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 md:p-8 border border-amber-200 shadow-lg mb-8">
            <h3 className="font-cinzel text-xl text-amber-800 mb-4">Cosa offriamo</h3>
            <ul className="space-y-3">
              {service.features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-600 rounded-full" />
                  <span className="text-stone-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Gallery Link */}
        {service.showGalleryLink && (
          <div className="text-center mb-6">
            <button
              onClick={onNavigateToGallery}
              className="inline-flex items-center gap-3 px-8 py-4 bg-amber-700 hover:bg-amber-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all font-medium"
            >
              <span>Scopri i nostri lavori nella Galleria</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Instagram Link */}
        {service.showInstagramLink && (
          <div className="text-center">
            <a
              href={siteInfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all font-medium"
            >
              <Instagram className="w-5 h-5" />
              <span>Scopri i prodotti su Instagram</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProdottiSection;
